<script setup>
import { onMounted, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { FilterMatchMode } from 'primevue/api'
import LocationService from '@/apps/dailyTech/inventario-ti/services/locations.service'
import Icon from '@/components/Icon/index.vue'
import Swal from 'sweetalert2'

const toast = useToast()
const _LocationService = ref(new LocationService())
const modalLocation = ref(false)
const listLocations = ref([])
const location = ref({})
const submitted = ref(false)
const isEdit = ref(false)
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS }
})
const listTipoSede = ref([
  { label: 'PROPIA', value: 'PROPIA' },
  { label: 'SUBCONTRATADA', value: 'SUBCONTRATADA' },
  { label: 'EXTERNA', value: 'EXTERNA' }
])

// Obtener las ubicaciones
const fetchLocations = async () => {
  try {
    const { status, data } = await _LocationService.value.getLocations()
    if (status === 200) {
      listLocations.value = data
    }
  } catch (error) {
    console.error(error.message)
  }
}

// Abrir el modal
const openModalLocation = (item) => {
  modalLocation.value = true
  submitted.value = false

  if (item) {
    location.value = { ...item, estado: item.estado === 'ACTIVO' }
    isEdit.value = true
  } else {
    location.value = {
      id: null,
      name: '',
      location_id: 0,
      address: null,
      depto: null,
      ciudad: null,
      estado: null,
      tipo_sede: null,
      deleted: 0
    }
    isEdit.value = false
  }
}

// Cerrar el modal
const hideModalLocation = () => {
  modalLocation.value = false
  submitted.value = false
}

// Crear una ubicacion
const createLocation = async () => {
  try {
    // Validar
    if (!location.value.name || !location.value.depto || !location.value.ciudad || !location.value.tipo_sede) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Ingrese los campos requeridos', life: 3000 })
      return
    }
    const { status } = await _LocationService.value.createLocation(location.value)
    if (status === 200) {
      toast.add({ severity: 'success', summary: 'Exito', detail: 'Ubicación creada exitosamente', life: 3000 })
      await fetchLocations()
      hideModalLocation()
      modalLocation.value = false
      location.value = {}
    }
  } catch (error) {
    console.error(error.message)
  }
}

// Actualizar una ubicacion
const updateLocation = async () => {
  try {
    // Validar
    if (!location.value.id || !location.value.name || !location.value.depto || !location.value.ciudad || !location.value.tipo_sede) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Ingrese los campos requeridos', life: 3000 })
      return
    }

    const { status } = await _LocationService.value.editLocation(location.value.id, location.value)
    if (status === 200) {
      toast.add({ severity: 'success', summary: 'Exito', detail: 'Ubicación actualizada exitosamente', life: 3000 })
      await fetchLocations()
      hideModalLocation()
      modalLocation.value = false
      location.value = {}
    }
  } catch (error) {
    console.error(error.message)
  }
}

// Guardar o actualizar una ubicacion
const onSubmit = async () => {
  submitted.value = true
  if (isEdit.value && location.value.id) {
    await updateLocation()
  } else {
    await createLocation()
  }
}

// Eliminar una ubicacion
const deleteLocation = async (id) => {
  try {
    if (id) {
      Swal.fire({
        icon: 'warning',
        title: 'Advertencia',
        text: 'Desea eliminar la ubicacion?, esta acción no se puede revertir.',
        showDenyButton: true,
        denyButtonText: 'No, Cancelar',
        confirmButtonText: 'Si, Eliminar'
      }).then((result) => {
        if (result.isConfirmed) {
          _LocationService.value.deleteLocation(id).then(({ status }) => {
            if (status !== 200) return

            toast.add({ severity: 'success', summary: 'Exito', detail: 'Ubicación eliminada exitosamente', life: 3000 })
            fetchLocations()
            hideModalLocation()
            modalLocation.value = false
            location.value = {}
          }).catch((err) => {
            console.error(err.message)
            if (err.response.status) {
              toast.add({ severity: 'error', summary: 'Error', detail: 'No se ha podido eliminar el ubicacion', life: 3000 })
            }
          })
        }
      })
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: 'No se ha podido eliminar el ubicacion', life: 3000 })
    }
  } catch (error) {
    console.error(error.message)
  }
}

// Hook onMounted
onMounted(async () => {
  await fetchLocations()
})
</script>

<template>
  <Toast />
  <Toolbar class="box mt-4 py-2 px-4">
    <template #start>
      <Button label="Regresar" icon="pi pi-arrow-left" class="p-button-sm" @click="$router.push({ name: 'daily-tech.inventario-ti.configuracion'})"/>
    </template>
    <template #end>
      <Button
        label="Agregar"
        icon="pi pi-plus"
        size="small"
        severity="success"
        @click="openModalLocation(null)"
      />
    </template>
  </Toolbar>

  <!-- Listado de los ubicaciones -->
  <div class="box mt-4 intro-x">
    <DataTable
      showGridlines
      ref="dt"
      :value="listLocations"
      class="p-datatable-sm p-4 text-sm"
      dataKey="id"
      :paginator="true"
      :rows="10"
      :filters="filters"
      sortField="id"
      :sortOrder="-1"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :rowsPerPageOptions="[5,10,25,50]"
      currentPageReportTemplate="Mostrando registros del {first} al {last} de un total de {totalRecords} registros"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header p-d-flex p-flex-column p-flex-md-row p-jc-md-between">
          <h3 class="p-mb-2 p-m-md-0 p-as-md-center text-lg">Listado de ubicaciones</h3>
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Buscar..." />
          </span>
        </div>
      </template>

      <!-- Columnas -->
      <template #empty>
        <div class="flex flex-col items-center justify-center" style="height: 500px">
          <Icon icon="fxemoji:cityscape" class="text-gray-200" style="font-size: 15rem; opacity: 0.3"/>
          <span class="text-gray-400 text-2xl">No se encontraron registros</span>
          <span class="text-gray-300">No hemos encontrado ningún elemento, pruebe con otro filtro u opción</span>
        </div>
      </template>
      <template #loading> Cargando la información, por favor espere... </template>
      <Column field="id" header="Id" :sortable="true"></Column>
      <Column field="name" header="Nombre" :sortable="true"></Column>
      <Column field="address" header="Dirección" :sortable="true"></Column>
      <Column field="depto" header="Departamento" :sortable="true"></Column>
      <Column field="ciudad" header="Ciudad" :sortable="true"></Column>
      <Column field="tipo_sede" header="Tipo Sede" :sortable="true"></Column>
      <Column field="estado" header="Estado" :sortable="true">
        <template #body="{ data }">
          <span :class="'bank-badge px-2 py-1 text-xs font-bold leading-none text-white rounded ' + (data.estado === 'ACTIVO' ? 'bg-green-700' : 'bg-red-700')">{{data.estado}}</span>
        </template>
      </Column>
      <Column :exportable="false" header="Acciones" style="text-align: center">
        <template #body="{ data }">
          <Button icon="pi pi-pencil" class="p-button-rounded p-button-warning p-mr-2" @click="openModalLocation(data)" />
          <Button icon="pi pi-trash" class="p-button-rounded p-button-danger" @click="deleteLocation(data.id)" />
        </template>
      </Column>
    </DataTable>
  </div>

  <!-- Modal formulario ubicaciones -->
  <Dialog
    v-model:visible="modalLocation"
    :style="{width: '450px'}"
    :modal="true"
    class="p-fluid"
    :pt="{
      header: { style: 'padding: 1rem;' }
    }"
  >
    <template #header>
      <h3 :class="{'text-yellow-700': isEdit, 'text-green-700': !isEdit}" class="font-bold text-xl">{{isEdit ? 'Editar' : 'Crear'}} ubicacion</h3>
    </template>
    <!-- Nombre -->
    <div class="p-field">
      <label for="name">Nombre</label>
      <InputText
        id="name"
        v-model="location.name"
        required="true"
        autofocus
        :class="{'p-invalid': submitted && !location.name}"
        class="p-fluid"
      />
      <small class="p-error" v-if="submitted && !location.name">El nombre es requerido.</small>
    </div>
    <!-- Dirección -->
    <div class="p-field">
      <label for="address">Dirección</label>
      <InputText
        id="address"
        v-model="location.address"
        autofocus
        class="p-fluid"
      />
    </div>
    <!-- Departamento -->
    <div class="p-field">
      <label for="depto">Departamento</label>
      <InputText
        id="depto"
        v-model="location.depto"
        required="true"
        autofocus
        :class="{'p-invalid': submitted && !location.depto}"
        class="p-fluid"
      />
      <small class="p-error" v-if="submitted && !location.depto">El departamento es requerido.</small>
    </div>
    <!-- Ciudad -->
    <div class="p-field">
      <label for="ciudad">Ciudad</label>
      <InputText
        id="ciudad"
        v-model="location.ciudad"
        required="true"
        autofocus
        :class="{'p-invalid': submitted && !location.ciudad}"
        class="p-fluid"
      />
      <small class="p-error" v-if="submitted && !location.ciudad">La ciudad es requerida.</small>
    </div>
    <!-- Tipo sede -->
    <div class="p-field">
      <label for="tipo_sede">Tipo Sede</label>
      <Dropdown
        id="tipo_sede"
        v-model="location.tipo_sede"
        :options="listTipoSede"
        optionLabel="label"
        optionValue="value"
        placeholder="Seleccione una opción"
        :filter="true"
        scrollHeight="300px"
        filterPlaceholder="Buscar por nombre"
        autoFilterFocus
        class="w-full"
        :class="{'p-invalid': submitted && !location.tipo_sede}"
        :pt="{
          panel: { style: 'font-size: 0.75rem; max-width: 100px;' },
          item: { style: 'padding: 0.3rem 0.8rem; line-height: 1.4; word-wrap: break-word; white-space: normal; border-bottom: 1px dashed #eee' }
        }"
      />
      <small class="p-error" v-if="submitted && !location.tipo_sede">El tipo sede es requerido.</small>
    </div>
    <div class="p-field">
      <label for="estado">Estado</label>
      <InputSwitch v-model="location.estado" id="estado" class="p-d-block"/>
    </div>

    <template #footer class="p-text-bold">
      <Button label="Cancelar" icon="pi pi-times" class="p-button-danger p-button-sm" @click="hideModalLocation"/>
      <Button label="Guardar" icon="pi pi-check" class="p-button-success p-button-sm" @click="onSubmit" />
    </template>
  </Dialog>
</template>

<style scoped>

</style>
